import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  BoxedDeliveryIcon,
  BoxedFingerIcon,
  BoxedRingIcon,
  BoxedRingOr4Icon,
  BoxedSizeKitIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import RichText from '@/app/components/RichText';
import useDisplayBestBuy from '@/app/hooks/useDisplayBestBuy';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { tValueBestBuy, tValueSizing } from './tValues';

interface ProductSizingModalProps {
  readonly onSizeLinkClick: () => void;
  readonly isOuraRing4Wizard: boolean;
}

interface HeaderProps {
  readonly children: ReactNode;
}

interface ContentProps {
  readonly children: ReactNode;
}

const HeaderText = ({ children }: HeaderProps) => {
  return (
    <TypographyV3
      Element="h4"
      variant="h4"
      color="inherit"
      className="text-xl font-normal"
    >
      {children}
    </TypographyV3>
  );
};

const ContentText = ({ children }: ContentProps) => {
  return (
    <div className="pb-3">
      <TypographyV3 color="inherit" Element="div" className="text-xs">
        {children}
      </TypographyV3>
    </div>
  );
};

const MobileSizeModal = ({
  onSizeLinkClick,
  isOuraRing4Wizard,
}: ProductSizingModalProps) => {
  const isBestBuyCountry = useDisplayBestBuy();
  const isBestBuyLinkActive = useFeatureFlag('best-buy-store-locator-page');
  const t = useTranslations();

  return (
    <section className="text-sandstone-500">
      <TypographyV3
        Element="h2"
        variant="h3"
        color="inherit"
        className="pt-5 text-4xl"
      >
        {t('pdp_horizon_sizing_modal_title')}
      </TypographyV3>
      <TypographyV3
        Element="h3"
        variant="h4"
        color="inherit"
        className="py-3.5 text-base font-normal"
      >
        {t('pdp_size_modal_choose_the_right')}
      </TypographyV3>

      <Accordion
        hiddenIndices={[isBestBuyLinkActive && isBestBuyCountry ? 0 : 3]}
      >
        <AccordionHeader>
          <HeaderText>{t('pdp_size_modal_size_at_home')}</HeaderText>
        </AccordionHeader>
        <AccordionContent>
          <ContentText>
            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedSizeKitIcon />
              <TypographyV3
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_1_title')}
              </TypographyV3>
            </div>
            <TypographyV3 Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_1_content')}
            </TypographyV3>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedDeliveryIcon />
              <TypographyV3
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_2_title')}
              </TypographyV3>
            </div>
            <TypographyV3 Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_2_content_04_2025')}
            </TypographyV3>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedFingerIcon />
              <TypographyV3
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_3_title')}
              </TypographyV3>
            </div>
            <TypographyV3 Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_3_content')}
            </TypographyV3>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              {isOuraRing4Wizard ? <BoxedRingOr4Icon /> : <BoxedRingIcon />}
              <TypographyV3
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_4_title')}
              </TypographyV3>
            </div>
            <TypographyV3 Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_4_content')}
            </TypographyV3>
          </ContentText>
        </AccordionContent>
        <AccordionHeader>
          <HeaderText>
            <RichText messageKey="pdp_size_modal_size_in_person_best_buy_title" />
          </HeaderText>
        </AccordionHeader>
        <AccordionContent>
          <ContentText>
            <TypographyV3
              Element="p"
              color="inherit"
              className="py-3.5 text-base font-normal"
            >
              <RichText
                messageKey="pdp_size_modal_size_in_person_best_buy_content"
                tValues={tValueBestBuy}
              />
            </TypographyV3>
          </ContentText>
        </AccordionContent>
      </Accordion>
      <TypographyV3
        Element="p"
        color="inherit"
        className="py-3.5 text-base font-normal"
      >
        <RichText
          messageKey="pdp_size_modal_size_video_text_link"
          tValues={tValueSizing(onSizeLinkClick)}
        />
      </TypographyV3>
    </section>
  );
};
export default MobileSizeModal;
